import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import { toast } from "react-toastify";
import { Col, Row, Table, Alert } from "reactstrap";
import { Breadcrumbs, H5 } from "../../AbstractElements";
import { useLoader } from "../../_helper/LoaderContext";
import PrivilegeContext from "../../_helper/PrivilegeContext";
import { useCurrency } from "../../_helper/CurrencyContext";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import { canShowPatientAccounts } from "../../utils/pharmacyConnection";
import CollectPaymentModal from "./components/CollectPaymentModal";
import InvoicePreviewModal from "./components/InvoicePreviewModal";
import InvoiceStatusBadge from "./components/InvoiceStatusBadge";
import PatientAccountPaidAmount from "./components/PatientAccountPaidAmount";
import { getPatientInvoiceDetailsAPI, payRemainingSalesAPI } from "./PatientAccountsService";
import { getGeneralServiceMaster, getLabTestMaster } from "../PharmacySales/SalesService";
import {
	buildPharmacyItemsFromBilling,
	buildServiceItemsFromBilling,
	enrichInvestigationsWithMaster,
	enrichServiceItemsWithMaster,
	getBillingBaseCurrency,
	hasPatientAccountBilling,
	initializePatientAccountBillingItems,
	parseGeneralServiceMasterResponse,
	parseLabMasterResponse,
	pinBillingLinesToBaseCurrency,
	resolveTreatmentBillableServiceItems,
	resolveTreatmentCollectPaymentInvestigations,
} from "../PharmacySales/NewSales/utils/billingUtils";
import { BillingPharmacyMedicinesTable } from "../PharmacySales/NewSales/components/BillingPharmacySection";
import "../PharmacySales/NewSales/components/BillingPaymentModal.scss";
import {
	applyPayRemainingSalesResponse,
	buildSectionLinesWithDiscountTax,
	buildCollectAlignedAmountBreakdown,
	buildPatientAccountDetailLines,
	buildPatientAccountFinancialSummary,
	getInvoiceSectionDiscountTax,
	buildPatientAccountPaymentHistory,
	buildPayRemainingSalesPayload,
	buildTreatmentAdditionalServices,
	buildTreatmentPackageDetails,
	buildTreatmentPackagePaymentHistory,
	formatAmount,
	formatDateList,
	formatDateTime,
	formatGender,
	formatInvoiceType,
	formatPaymentHistoryPaidAmount,
	getCollectPaymentAppointmentId,
	getPatientAccountPatientId,
	getPatientDisplayName,
	isTreatmentBillableServiceType,
	isTreatmentPackageInvoice,
	loadPatientAccountRecord,
	mergeDispensePrescriptionData,
	normalizePatientAccountItem,
	normalizePaymentStatus,
	savePatientAccountRecord,
} from "./utils/patientAccountUtils";
import { resolveInvoicePresignedUrl } from "../PharmacySales/NewSales/utils/invoiceUrlUtils";
import { toMoneyNumber } from "../../utils/financeEngine";
import "./patient-accounts-style.scss";

const MENU_KEY = "patient_accounts";
const SUB_PERMISSION_KEY = "patient_accounts";

const isValidInvoiceRecord = (details) => {
	if (!details || typeof details !== "object") return false;
	if (details.status && details.message && !details._id && !details.appointment_invoice) {
		return false;
	}
	return Boolean(
		details._id ||
			details.appointment_invoice ||
			details.patient ||
			details.package_payment_details
	);
};

const SessionStatusBadge = ({ status }) => {
	const { t } = useTranslation();
	const normalized = String(status || "pending").toLowerCase();
	const isCompleted = normalized === "completed";

	return (
		<span
			className={`tp-session-status ${
				isCompleted ? "tp-session-status-completed" : "tp-session-status-pending"
			}`}
		>
			{isCompleted ? t("Completed") : t("Pending")}
		</span>
	);
};

/**
 * Consultation / Lab footer: Discount and Tax as separate rows (not line columns).
 * Matches invoice detail layout — Discount (red -), Tax/VAT (green +), Total.
 */
const ServiceSectionFooter = ({
	t,
	subtotal,
	discount,
	tax,
	netTotal,
	currency,
	getCurrencySymbol,
	taxLabel,
}) => {
	const hasDiscount = toMoneyNumber(discount) > 0;
	const hasTax = toMoneyNumber(tax) > 0;
	const hasAdjustments = hasDiscount || hasTax;
	const lineSubtotal = toMoneyNumber(subtotal ?? netTotal);

	return (
		<tfoot>
			{hasAdjustments && (
				<tr className="tp-service-footer-row tp-service-footer-row--subtotal">
					<td colSpan={4} />
					<td className="text-end fw-semibold">{t("Total")}</td>
					<td className="text-end fw-semibold">
						{formatAmount(lineSubtotal, currency, getCurrencySymbol)}
					</td>
				</tr>
			)}
			{hasDiscount && (
				<tr className="tp-service-footer-row tp-service-footer-row--discount">
					<td colSpan={4} />
					<td className="text-end fw-semibold">{t("Discount")}</td>
					<td className="text-end fw-semibold text-danger">
						-{formatAmount(discount, currency, getCurrencySymbol)}
					</td>
				</tr>
			)}
			{hasTax && (
				<tr className="tp-service-footer-row tp-service-footer-row--tax">
					<td colSpan={4} />
					<td className="text-end fw-semibold">{taxLabel || t("Tax")}</td>
					<td className="text-end fw-semibold text-success">
						+{formatAmount(tax, currency, getCurrencySymbol)}
					</td>
				</tr>
			)}
			<tr className="tp-service-footer-row tp-service-footer-row--total">
				<td colSpan={4} />
				<td className="text-end fw-bold">{t("Total")}</td>
				<td className="text-end fw-bold">
					{formatAmount(netTotal, currency, getCurrencySymbol)}
				</td>
			</tr>
		</tfoot>
	);
};

const PaymentHistoryFooter = ({
	t,
	totalAmount,
	currency,
	foreignPayments = [],
	getCurrencySymbol,
	variant = "standard",
}) => (
	<tfoot>
		<tr className="tp-service-footer-row tp-service-footer-row--total">
			{variant === "treatment" ? (
				<>
					<td colSpan={2} />
					<td className="text-end fw-bold">{t("Total")}</td>
					<td className="text-end fw-bold">
						<PatientAccountPaidAmount
							amount={totalAmount}
							currency={currency}
							foreignPayments={foreignPayments}
							getCurrencySymbol={getCurrencySymbol}
						/>
					</td>
					<td />
				</>
			) : (
				<>
					<td />
					<td className="text-end fw-bold">{t("Total")}</td>
					<td className="text-end fw-bold">
						<PatientAccountPaidAmount
							amount={totalAmount}
							currency={currency}
							foreignPayments={foreignPayments}
							getCurrencySymbol={getCurrencySymbol}
						/>
					</td>
					<td colSpan={2} />
				</>
			)}
		</tr>
	</tfoot>
);

const PatientAccountDetails = () => {
	const { invoiceId } = useParams();
	const location = useLocation();
	const { t } = useTranslation();
	const { setLoading } = useLoader();
	const { privileges } = useContext(PrivilegeContext);
	const { getCurrencySymbol, allowedCurrencies, convertCurrency } = useCurrency();
	const { pharmacyPOS } = usePharmacy();
	const history = useNavigate();

	const [skeletonLoading, setSkeletonLoading] = useState(true);
	const [record, setRecord] = useState(null);
	const [viewView, setViewView] = useState(false);
	const [collectView, setCollectView] = useState(false);
	const [previewModal, setPreviewModal] = useState(false);
	const [previewUrl, setPreviewUrl] = useState("");
	const [previewTitle, setPreviewTitle] = useState("");
	const [previewLoading, setPreviewLoading] = useState(false);
	const [collectModal, setCollectModal] = useState(false);
	const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
	const [labMasterList, setLabMasterList] = useState([]);
	const [generalMasterList, setGeneralMasterList] = useState([]);

	const getPermission = useCallback(() => {
		if (privileges?.permissions) {
			privileges.permissions.forEach((element) => {
				if (element.menu_key === MENU_KEY && element.sub_permissions) {
					element.sub_permissions.forEach((child) => {
						if (child.key === SUB_PERMISSION_KEY && child.actions) {
							child.actions.forEach((action) => {
								if (action.key === "view" && action.allowed) setViewView(true);
								if ((action.key === "collect_payment" || action.key === "update") && action.allowed) {
									setCollectView(true);
								}
							});
						}
					});
				}
				if (element.menu_key === MENU_KEY && element.actions) {
					element.actions.forEach((action) => {
						if (action.key === "view" && action.allowed) setViewView(true);
						if ((action.key === "collect_payment" || action.key === "update") && action.allowed) {
							setCollectView(true);
						}
					});
				}
			});
		} else {
			setViewView(true);
			setCollectView(true);
		}
	}, [privileges]);

	useEffect(() => {
		getPermission();
	}, [getPermission]);

	useEffect(() => {
		if (!invoiceId) {
			history(`${process.env.PUBLIC_URL}/patient-accounts`);
			return;
		}

		const loaded = loadPatientAccountRecord(invoiceId, location.state);
		if (!loaded) {
			toast.error(t("Invoice details not found. Please open from the list."));
			history(`${process.env.PUBLIC_URL}/patient-accounts`);
			return;
		}

		setRecord(loaded);
		savePatientAccountRecord(invoiceId, loaded);
		setSkeletonLoading(false);
	}, [history, invoiceId, location.state, t]);

	const fetchInvoiceDetails = useCallback(async () => {
		if (!invoiceId) return;

		const clinicId = pharmacyPOS?.hospital_id || "";
		if (!clinicId) return;

		try {
			const response = await getPatientInvoiceDetailsAPI({
				invoice_id: invoiceId,
				invoiceId,
				clinic_id: clinicId,
			});

			if (response?.data?.status === "error") return;

			const details =
				response?.data?.data?.invoice_details ||
				response?.data?.data ||
				response?.data;

			if (isValidInvoiceRecord(details)) {
				setRecord(details);
				savePatientAccountRecord(invoiceId, details);
			}
		} catch (error) {
			console.error("Error fetching patient invoice details:", error);
		}
	}, [invoiceId, pharmacyPOS?.hospital_id]);

	useEffect(() => {
		fetchInvoiceDetails();
	}, [fetchInvoiceDetails]);

	const fetchMasterLists = useCallback(async () => {
		const clinicId = pharmacyPOS?.hospital_id;
		if (!clinicId) return;

		try {
			const [labResponse, generalResponse] = await Promise.all([
				getLabTestMaster({ clinic_id: clinicId, search: "" }),
				getGeneralServiceMaster({ clinic_id: clinicId, search: "" }),
			]);
			setLabMasterList(parseLabMasterResponse(labResponse));
			setGeneralMasterList(parseGeneralServiceMasterResponse(generalResponse));
		} catch (error) {
			console.error("Error loading master lists:", error);
		}
	}, [pharmacyPOS?.hospital_id]);

	useEffect(() => {
		if (record) {
			fetchMasterLists();
		}
	}, [fetchMasterLists, record]);

	const handleBack = () => {
		history(`${process.env.PUBLIC_URL}/patient-accounts`);
	};

	const handleDispenseClick = async () => {
		if (!record) return;

		let prescriptionData = record;
		const clinicId = pharmacyPOS?.hospital_id;

		if (invoiceId && clinicId) {
			try {
				setLoading(true, t("Loading invoice..."));
				const response = await getPatientInvoiceDetailsAPI({
					invoice_id: invoiceId,
					invoiceId,
					clinic_id: clinicId,
				});
				const details =
					response?.data?.data?.invoice_details ||
					response?.data?.data ||
					response?.data;
				if (
					response?.data?.status !== "error" &&
					details &&
					typeof details === "object" &&
					(details._id || details.appointment_invoice || details.billing)
				) {
					prescriptionData =
						mergeDispensePrescriptionData(prescriptionData, details) || details;
				}
			} catch (error) {
				console.error("Error loading invoice for dispense:", error);
			} finally {
				setLoading(false);
			}
		}

		savePatientAccountRecord(invoiceId, prescriptionData);
		history(`${process.env.PUBLIC_URL}/new-sales`, {
			state: { prescriptionData },
		});
	};

	const handleCollectSubmit = async (payload) => {
		const appointmentId =
			payload.appointmentId || getCollectPaymentAppointmentId(record);

		if (!appointmentId) {
			toast.error(t("Appointment not found for this invoice."));
			return;
		}

		try {
			setIsSubmittingPayment(true);
			setLoading(true, t("Payment processing..."));
			const invoiceRecord = payload.invoiceRecord || record;
			const response = await payRemainingSalesAPI(
				buildPayRemainingSalesPayload({
					appointmentId,
					paidAmount: payload.paidAmount,
					paymentType: payload.paymentMode,
					currencyType: payload.currency,
					isSettled: payload.markAsPaid,
					isConversion: payload.isConversion ?? payload.currency !== payload.baseCurrency,
					invoiceRecord,
					billing: payload.billing,
					patientId: payload.patientId || getPatientAccountPatientId(invoiceRecord),
					patientName:
						payload.patientName || getPatientDisplayName(invoiceRecord?.patient),
				})
			);
			if (response?.data?.status === "success") {
				toast.success(response.data.message || t("Payment collected successfully."));
				setCollectModal(false);
				const apiData = response.data.data;
				const merged = applyPayRemainingSalesResponse(
					invoiceRecord,
					apiData,
					payload.billing
				);
				setRecord(merged);
				savePatientAccountRecord(invoiceId, merged);
				if (!apiData?.sales) {
					await fetchInvoiceDetails();
				}
			} else {
				console.log(response?.data?.message);
				// toast.error(response?.data?.message || t("Unable to collect payment."));
			}
		} catch (error) {
			console.error("Error collecting payment:", error);
		} finally {
			setIsSubmittingPayment(false);
			setLoading(false);
		}
	};

	const normalized = useMemo(
		() => (record ? normalizePatientAccountItem(record) : null),
		[record]
	);

	const financialSummary = useMemo(
		() => buildPatientAccountFinancialSummary(record),
		[record]
	);

	const paymentHistory = useMemo(() => buildPatientAccountPaymentHistory(record), [record]);
	const isTreatmentPackage = useMemo(
		() => isTreatmentPackageInvoice(record),
		[record]
	);

	const treatmentPackageDetails = useMemo(
		() => (isTreatmentPackage ? buildTreatmentPackageDetails(record) : null),
		[isTreatmentPackage, record]
	);
	
	const treatmentPaymentHistory = useMemo(
		() =>
			isTreatmentPackage
				? buildTreatmentPackagePaymentHistory(record, getCurrencySymbol)
				: [],
		[getCurrencySymbol, isTreatmentPackage, record]
	);

	const paymentStatus =
		record?.appointment_invoice?.status || record?.billing?.payment?.status || record?.status;
	const normalizedStatus = normalizePaymentStatus(paymentStatus);
	const isPaid = normalizedStatus === "paid";

	const currency = financialSummary.currency || normalized?.currency || "INR";
	const patientName = getPatientDisplayName(record?.patient);
	const fileNumber = record?.patient?.health_id || record?.patient?.file_no || "-";
	const patientAddress = record?.patient?.address || "-";
	const patientGender = formatGender(record?.patient?.gender);
	const contactNumber = record?.patient?.mobile
		? `${record?.patient?.phone_code || ""} ${record.patient.mobile}`.trim()
		: "—";
	const email = record?.patient?.email || "—";
	const doctorName = record?.doctor?.name || "-";
	const doctorContact = record?.doctor?.mobile
		? `${record?.doctor?.phone_code || ""} ${record.doctor.mobile}`.trim()
		: "-";
	const doctorEmail = record?.doctor?.email || "-";
	const doctorGender = formatGender(
		record?.doctor?.gender ||
			record?.package_payment_details?.session_details?.[0]?.session?.appointment
				?.doctor_gender
	);

	const additionalServices = useMemo(() => {
		if (!record) return [];

		// Same data source resolution as Collect Payment modal:
		// resolveTreatmentBillableServiceItems for treatment packages,
		// initializePatientAccountBillingItems for standard patient account records.
		let services = isTreatmentPackage
			? resolveTreatmentBillableServiceItems(record, currency)
			: initializePatientAccountBillingItems(
					record,
					currency,
					allowedCurrencies
				).serviceItems;

		// Consultation fallback when invoice/billing service arrays are empty
		if (services.length === 0) {
			services = buildPatientAccountDetailLines(record)
				.filter((item) => isTreatmentBillableServiceType(item.service_type))
				.map((item) => ({
					...item,
					unit_price: item.charges ?? item.unit_price ?? item.price ?? 0,
					price: item.charges ?? item.unit_price ?? item.price ?? 0,
				}));
		}

		const baseCurrency = getBillingBaseCurrency(record, currency);

		const enriched =
			generalMasterList.length > 0
				? enrichServiceItemsWithMaster(
						services,
						generalMasterList,
						currency,
						allowedCurrencies
					)
				: services;

		return pinBillingLinesToBaseCurrency(enriched, baseCurrency);
	}, [allowedCurrencies, currency, generalMasterList, isTreatmentPackage, record]);

	// Keep Invoice Summary Additional Services in sync with the table amounts.
	const additionalServicesSection = useMemo(() => {
		const sectionAdj = getInvoiceSectionDiscountTax(record, "services");
		return buildSectionLinesWithDiscountTax(
			additionalServices,
			sectionAdj.discount,
			sectionAdj.tax,
			(item) => {
				const qty = Math.max(1, Number(item.quantity || item.qty) || 1);
				const unit = toMoneyNumber(item.unit_price ?? item.price ?? item.charges ?? 0);
				return unit > 0 ? unit * qty : toMoneyNumber(item.amount ?? item.sub_total ?? 0);
			}
		);
	}, [additionalServices, record]);

	const additionalServicesWithTax = additionalServicesSection.rows;

	const additionalServicesTotal = useMemo(
		() => toMoneyNumber(additionalServicesSection.netTotal),
		[additionalServicesSection.netTotal]
	);

	const labInvestigationsSection = useMemo(() => {
		if (!record) {
			return {
				rows: [],
				showSubtotalRow: false,
				subtotal: 0,
				discount: 0,
				tax: 0,
				netTotal: 0,
			};
		}

		// Use the same resolution logic as the Collect Payment modal so that
		// billing-only extra investigations (added after the appointment invoice
		// was created) are also shown here.
		let items = isTreatmentPackage
			? resolveTreatmentCollectPaymentInvestigations(
					record,
					currency,
					allowedCurrencies
				)
			: initializePatientAccountBillingItems(
					record,
					currency,
					allowedCurrencies
				).investigationItems;

		// Fallback: lab lines from combined invoice detail lines
		if (items.length === 0) {
			items = buildPatientAccountDetailLines(record)
				.filter((item) => {
					const type = String(item.service_type || "").toLowerCase();
					return type === "laboratory" || type === "lab" || type === "investigation";
				})
				.map((item) => ({
					...item,
					test_name: item.service_name || item.test_name || "-",
					type: item.service_type || "laboratory",
					unit_price: item.charges ?? item.unit_price ?? item.price ?? 0,
					price: item.charges ?? item.unit_price ?? item.price ?? 0,
					priceResolved:
						toMoneyNumber(item.amount) > 0 ||
						toMoneyNumber(item.charges ?? item.unit_price ?? item.price) > 0,
				}));
		}

		if (items.length === 0) {
			return {
				rows: [],
				showSubtotalRow: false,
				subtotal: 0,
				discount: 0,
				tax: 0,
				netTotal: 0,
			};
		}

		const withPriceResolved = (list) =>
			list.map((item) => ({
				...item,
				priceResolved:
					item.priceResolved === true ||
					toMoneyNumber(item.amount) > 0 ||
					toMoneyNumber(item.unit_price ?? item.price) > 0,
			}));

		const needsMasterFill = items.some(
			(item) =>
				toMoneyNumber(item.amount) <= 0 &&
				toMoneyNumber(item.unit_price ?? item.price) <= 0
		);
		const resolved =
			!needsMasterFill || labMasterList.length === 0
				? withPriceResolved(items)
				: enrichInvestigationsWithMaster(
						items,
						labMasterList,
						currency,
						allowedCurrencies
					);

		const sectionAdj = getInvoiceSectionDiscountTax(record, "investigations");
		return buildSectionLinesWithDiscountTax(
			resolved,
			sectionAdj.discount,
			sectionAdj.tax,
			(item) => {
				const qty = Math.max(1, Number(item.quantity) || 1);
				const unit = toMoneyNumber(item.unit_price ?? item.price ?? 0);
				return unit > 0 ? unit * qty : toMoneyNumber(item.amount ?? 0);
			}
		);
	}, [allowedCurrencies, currency, isTreatmentPackage, labMasterList, record]);

	const labInvestigations = labInvestigationsSection.rows;

	const labInvestigationsTotal = useMemo(
		() => toMoneyNumber(labInvestigationsSection.netTotal),
		[labInvestigationsSection.netTotal]
	);

	const prescriptionBillingItems = useMemo(() => {
		if (!record) return [];

		let rawItems = [];

		if (hasPatientAccountBilling(record)) {
			const billingPharmacy = buildPharmacyItemsFromBilling(
				record?.billing?.pharmacy_items
			);
			if (billingPharmacy.length > 0) return billingPharmacy;
		}

		const invoicePharmacy = (record?.appointment_invoice?.services || [])
			.filter((item) => String(item.service_type || "").toLowerCase() === "pharmacy")
			.map((item, index) => {
				const qty = Math.max(1, Number(item.quantity) || 1);
				const unitPrice = Number(item.price) || 0;
				const amount = Number(item.amount ?? unitPrice * qty) || 0;
				return {
					item_id: item.service_id || item._id || `pharmacy-${index}`,
					item: item.service_name || "-",
					brand_name: item.service_name || "-",
					qty,
					unit_price: unitPrice,
					sub_total: item.sub_total ?? amount,
					tax: item.tax_amount ?? item.tax ?? 0,
					tax_amount: item.tax_amount ?? item.tax ?? 0,
					tax_percentage: item.tax_percentage ?? 0,
					net_total: item.net_total ?? amount,
					date: item.date || item.sale_date || "",
					discount_type: item.discount_type,
					discount: item.discount,
				};
			});

		if (invoicePharmacy.length > 0) {
			rawItems = invoicePharmacy;
		} else {
			const detailPharmacy = buildPatientAccountDetailLines(record)
				.filter((item) => String(item.service_type || "").toLowerCase() === "pharmacy")
				.map((item, index) => {
					const qty = Math.max(1, Number(item.quantity) || 1);
					const unitPrice = toMoneyNumber(item.charges ?? item.unit_price ?? item.price);
					const amount = toMoneyNumber(item.amount ?? unitPrice * qty);
					return {
						item_id: item.id || `pharmacy-detail-${index}`,
						item: item.service_name || "-",
						brand_name: item.service_name || "-",
						qty,
						unit_price: unitPrice,
						sub_total: item.sub_total ?? amount,
						tax: item.tax_amount ?? item.tax ?? 0,
						tax_amount: item.tax_amount ?? item.tax ?? 0,
						tax_percentage: item.tax_percentage ?? 0,
						net_total: item.net_total ?? amount,
						date: item.date || "",
					};
				});
			if (detailPharmacy.length > 0) {
				rawItems = detailPharmacy;
			} else {
				rawItems = (record?.prescriptions || []).map((item, index) => {
					const qty = Math.max(1, Number(item.qty) || 1);
					const unitPrice = Number(item.price) || 0;
					const amount = unitPrice * qty;
					return {
						item_id: item._id || item.medication_id || `rx-${index}`,
						item: item.medication_name || "-",
						medicine_name: item.salt_composition || "",
						product_form: item.product_form || "",
						qty,
						unit_price: unitPrice,
						sub_total: amount,
						tax: 0,
						tax_percentage: 0,
						net_total: amount,
						date: item.date || item.sale_date || "",
					};
				});
			}
		}

		return buildPharmacyItemsFromBilling(rawItems);
	}, [record]);

	const pharmacyDetailTableProps = useMemo(() => {
		const pharmacyStateCode =
			pharmacyPOS?.state_code ||
			pharmacyPOS?.stateCode ||
			pharmacyPOS?.address?.state_code ||
			"";
		const patientStateCode =
			record?.patient?.state_code ||
			record?.patient?.stateCode ||
			record?.appointment_invoice?.patient?.state_code ||
			"";
		return {
			posTaxType: pharmacyPOS?.taxType || "gst",
			pharmacyStateCode,
			patientStateCode,
			discountType: record?.billing?.summary?.pharmacy_discount_type || "Percentage",
			discountValue: record?.billing?.summary?.pharmacy_discount_value ?? 0,
			displayCurrency: currency,
			pharmacyCurrency: currency,
		};
	}, [currency, pharmacyPOS, record]);

	const showPrescriptionSection = prescriptionBillingItems.length > 0;

	/** Same Payment Summary rows/values as Collect Payment modal. */
	const collectAmountBreakdown = useMemo(
		() => buildCollectAlignedAmountBreakdown(record),
		[record]
	);

	const docPaymentHistory =
		record?.package_payment_details?.payment_history ||
		record?.appointment_invoice?.payment_history ||
		[];
	const latestPayment = docPaymentHistory[0] || {};

	const invoiceUrl =
		record?.appointment_invoice?.invoice ||
		record?.appointment_invoice?.package_invoice ||
		record?.package_payment_details?.payment?.invoice ||
		record?.invoiceUrl ||
		record?.invoice_url ||
		record?.invoice ||
		latestPayment.invoice ||
		"";

	const receiptUrl =
		record?.appointment_invoice?.receipt ||
		record?.receiptUrl ||
		record?.receipt_url ||
		record?.receipt ||
		latestPayment.receipt ||
		latestPayment.invoice ||
		"";

	const canPreviewInvoice =
		Boolean(invoiceUrl) && (isPaid || normalizedStatus === "partial_paid");
	const canPreviewReceipt =
		Boolean(receiptUrl) && (isPaid || normalizedStatus === "partial_paid");

	const openDocumentPreview = async (documentKey, title) => {
		const key = String(documentKey || "").trim();
		if (!key) return;

		try {
			setPreviewLoading(true);
			const presignedUrl = await resolveInvoicePresignedUrl(key);
			if (!presignedUrl) {
				toast.warning(t("Preview is not available."));
				return;
			}

			setPreviewUrl(presignedUrl);
			setPreviewTitle(title);
			setPreviewModal(true);
		} catch (error) {
			console.error("Error opening document preview:", error);
			toast.warning(
				error?.response?.data?.message ||
					t("Failed to load preview. Please try again.")
			);
		} finally {
			setPreviewLoading(false);
		}
	};

	const closeDocumentPreview = () => {
		setPreviewModal(false);
		setPreviewUrl("");
		setPreviewTitle("");
	};

	const pageTitle =
		normalizedStatus === "paid"
			? t("View Paid Invoice Details")
			: normalizedStatus === "partial_paid"
				? t("View Partial Invoice Details")
				: t("View Unpaid Invoice Details");

	if (!canShowPatientAccounts(pharmacyPOS)) {
		return (
			<>
				<Breadcrumbs mainTitle={pageTitle} parent={t("Patient Accounts")} title={pageTitle} />
				<Alert color="warning" className="mx-3">
					{t(
						"Patient Accounts is available only when your pharmacy is connected to a hospital and is enabled to collect payments."
					)}
				</Alert>
			</>
		);
	}

	if (!viewView && privileges?.permissions && !skeletonLoading) {
		return (
			<>
				<Breadcrumbs mainTitle={pageTitle} parent={t("Patient Accounts")} title={pageTitle} />
				<div className="card" style={{ borderRadius: "15px" }}>
					<div className="card-body text-center py-5">
						{t("You do not have permission to view this invoice.")}
					</div>
				</div>
			</>
		);
	}

	return (
		<>
			<Breadcrumbs mainTitle={pageTitle} parent={t("Patient Accounts")} title={pageTitle} />
			<div className="row patient-accounts-details">
				<div className="col-sm-12">
					<div className="card" style={{ borderRadius: "15px" }}>
						<div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
							<div className="d-flex align-items-center flex-wrap gap-2">
								<H5 className="mb-0">{pageTitle}</H5>
								{isTreatmentPackage && (
									<span className="badge tp-type-badge">{t("Treatment Package")}</span>
								)}
							</div>
							<div className="d-flex flex-wrap gap-2">
								{(viewView || collectView) && (
									<button
										type="button"
										className="btn btn-success"
										disabled={!record || skeletonLoading}
										onClick={handleDispenseClick}
									>
										<i className="fa fa-plus me-2" />
										{t("Dispense")}
									</button>
								)}
								<button
									type="button"
									className="btn btn-primary"
									disabled={!canPreviewInvoice || !viewView || previewLoading}
									onClick={() =>
										openDocumentPreview(invoiceUrl, t("Invoice Preview"))
									}
								>
									{previewLoading ? (
										<span className="spinner-border spinner-border-sm me-2" role="status" />
									) : (
										<i className="fa fa-file-text-o me-2" />
									)}
									{t("Invoice Preview")}
								</button>
								<button
									type="button"
									className="btn btn-outline-primary"
									disabled={!canPreviewReceipt || !viewView || previewLoading}
									onClick={() =>
										openDocumentPreview(receiptUrl, t("Receipt Preview"))
									}
								>
									{previewLoading ? (
										<span className="spinner-border spinner-border-sm me-2" role="status" />
									) : (
										<i className="fa fa-file-o me-2" />
									)}
									{t("Receipt Preview")}
								</button>
								{/* {isPartialOrUnpaid && collectView && (
									<button
										type="button"
										className="btn btn-primary"
										onClick={() => setCollectModal(true)}
									>
										<i className="fa fa-money me-2" />
										{t("Collect Payment")}
									</button>
								)} */}
								<button
									type="button"
									className="btn btn-outline-secondary"
									onClick={handleBack}
								>
									<i className="fa fa-arrow-circle-o-left me-2 fs-6" />
									{t("Back")}
								</button>
							</div>
						</div>

						<div className="card-body">
							{isTreatmentPackage ? (
								<div className="tp-view">
									<div className="tp-meta-strip mb-3">
										<div className="tp-meta-item">
											<span className="tp-meta-label">{t("Invoice Number")}</span>
											<span className="tp-meta-value">
												{skeletonLoading ? (
													<Skeleton width={130} />
												) : (
													normalized?.invoice_no || "-"
												)}
											</span>
										</div>
										<div className="tp-meta-item">
											<span className="tp-meta-label">{t("Invoice Date")}</span>
											<span className="tp-meta-value">
												{skeletonLoading ? (
													<Skeleton width={150} />
												) : (
													formatDateTime(normalized?.invoice_date)
												)}
											</span>
										</div>
										<div className="tp-meta-item">
											<span className="tp-meta-label">{t("Payment Status")}</span>
											<span className="tp-meta-value">
												{skeletonLoading ? (
													<Skeleton width={90} height={22} />
												) : (
													<InvoiceStatusBadge
														status={paymentStatus}
														isSettled={record?.appointment_invoice?.is_settled === true}
													/>
												)}
											</span>
										</div>
									</div>

									<Row className="g-3 mb-3">
										<Col md="6" xl="4">
											<div className="tp-details-card">
												<div className="tp-details-card-title">
													<i className="fa fa-user-o" />
													<span>{t("Patient Details")}</span>
												</div>
												<div className="tp-details-card-body">
													<div className="tp-detail-row">
														<span className="tp-detail-label">{t("Name")}</span>
														<span className="tp-detail-value">
															{skeletonLoading ? <Skeleton /> : patientName}
														</span>
													</div>
													<div className="tp-detail-row">
														<span className="tp-detail-label">{t("File No.")}</span>
														<span className="tp-detail-value">
															{skeletonLoading ? <Skeleton /> : fileNumber}
														</span>
													</div>
													<div className="tp-detail-row">
														<span className="tp-detail-label">{t("Address")}</span>
														<span className="tp-detail-value">
															{skeletonLoading ? <Skeleton /> : patientAddress}
														</span>
													</div>
													<div className="tp-detail-row">
														<span className="tp-detail-label">{t("Contact")}</span>
														<span className="tp-detail-value">
															{skeletonLoading ? <Skeleton /> : contactNumber}
														</span>
													</div>
													<div className="tp-detail-row">
														<span className="tp-detail-label">{t("Gender")}</span>
														<span className="tp-detail-value">
															{skeletonLoading ? <Skeleton /> : patientGender}
														</span>
													</div>
												</div>
											</div>
										</Col>
										<Col md="6" xl="4">
											<div className="tp-details-card">
												<div className="tp-details-card-title">
													<i className="fa fa-stethoscope" />
													<span>{t("Doctor Details")}</span>
												</div>
												<div className="tp-details-card-body">
													<div className="tp-detail-row">
														<span className="tp-detail-label">{t("Name")}</span>
														<span className="tp-detail-value">
															{skeletonLoading ? <Skeleton /> : doctorName}
														</span>
													</div>
													<div className="tp-detail-row">
														<span className="tp-detail-label">{t("Contact")}</span>
														<span className="tp-detail-value">
															{skeletonLoading ? <Skeleton /> : doctorContact}
														</span>
													</div>
													<div className="tp-detail-row">
														<span className="tp-detail-label">{t("Email")}</span>
														<span className="tp-detail-value">
															{skeletonLoading ? <Skeleton /> : doctorEmail}
														</span>
													</div>
													<div className="tp-detail-row">
														<span className="tp-detail-label">{t("Gender")}</span>
														<span className="tp-detail-value">
															{skeletonLoading ? <Skeleton /> : doctorGender}
														</span>
													</div>
												</div>
											</div>
										</Col>
										<Col md="12" xl="4">
											<div className="tp-details-card">
												<div className="tp-details-card-title">
													<i className="fa fa-medkit" />
													<span>{t("Treatment Details")}</span>
												</div>
												<div className="tp-details-card-body">
													<div className="tp-detail-row">
														<span className="tp-detail-label">{t("Treatment Package")}</span>
														<span className="tp-detail-value">
															{skeletonLoading ? (
																<Skeleton />
															) : (
																treatmentPackageDetails?.packageName || "-"
															)}
														</span>
													</div>
													<div className="tp-detail-row">
														<span className="tp-detail-label">{t("Total Sessions")}</span>
														<span className="tp-detail-value">
															{skeletonLoading ? (
																<Skeleton />
															) : (
																treatmentPackageDetails?.noOfSessions || "-"
															)}
														</span>
													</div>
													<div className="tp-detail-row">
														<span className="tp-detail-label">{t("Completed Sessions")}</span>
														<span className="tp-detail-value">
															{skeletonLoading ? (
																<Skeleton />
															) : (
																treatmentPackageDetails?.completedSessions ?? 0
															)}
														</span>
													</div>
													<div className="tp-detail-row">
														<span className="tp-detail-label">{t("Current Session")}</span>
														<span className="tp-detail-value">
															{skeletonLoading ? (
																<Skeleton />
															) : (
																treatmentPackageDetails?.currentSession || "-"
															)}
														</span>
													</div>
													<div className="tp-detail-row">
														<span className="tp-detail-label">{t("Treatment Start Date")}</span>
														<span className="tp-detail-value">
															{skeletonLoading ? (
																<Skeleton />
															) : (
																formatDateList(treatmentPackageDetails?.treatmentStartDate)
															)}
														</span>
													</div>
													<div className="tp-detail-row">
														<span className="tp-detail-label">{t("Clinic")}</span>
														<span className="tp-detail-value">
															{skeletonLoading ? (
																<Skeleton />
															) : (
																treatmentPackageDetails?.clinicName ||
																pharmacyPOS?.hospital?.name ||
																"-"
															)}
														</span>
													</div>
												</div>
											</div>
										</Col>
									</Row>

									<div className="tp-section-box mb-3">
										<div className="tp-section-title">
											<i className="fa fa-list-ul" />
											<span>{t("Sessions")}</span>
										</div>
										<div className="tp-section-body table-responsive">
											<Table className="tp-table mb-0">
												<thead>
													<tr>
														<th>#</th>
														<th>{t("Session")}</th>
														<th>{t("Description")}</th>
														<th>{t("Duration")}</th>
														<th>{t("Schedule")}</th>
														<th className="text-center">{t("Status")}</th>
													</tr>
												</thead>
												<tbody>
													{skeletonLoading ? (
														<tr>
															<td colSpan="6">
																<Skeleton count={3} />
															</td>
														</tr>
													) : treatmentPackageDetails?.sessions?.length > 0 ? (
														treatmentPackageDetails.sessions.map((session) => (
															<tr key={session.id}>
																<td>{session.index}</td>
																<td className="fw-semibold">{session.sessionName}</td>
																<td className="tp-muted-cell">{session.description}</td>
																<td>{session.duration}</td>
																<td>{session.schedule}</td>
																<td className="text-center">
																	<SessionStatusBadge status={session.status} />
																</td>
															</tr>
														))
													) : (
														<tr>
															<td colSpan="6" className="text-center text-muted">
																{t("No sessions found.")}
															</td>
														</tr>
													)}
												</tbody>
											</Table>
										</div>
									</div>

									<Row className="g-3 mb-3">
										<Col xs="6" xl="3">
											<div className="tp-stat-card tp-stat-card--price">
												<div className="tp-stat-label">{t("Package Price")}</div>
												<div className="tp-stat-value">
													{formatAmount(
														collectAmountBreakdown.packagePrice,
														currency,
														getCurrencySymbol
													)}
												</div>
												{collectAmountBreakdown.showAdditionalServices && (
													<div className="tp-stat-subtext">
														{t("Additional Services")}:{" "}
														{formatAmount(
															collectAmountBreakdown.servicesTotal,
															currency,
															getCurrencySymbol
														)}
													</div>
												)}
											</div>
										</Col>
										<Col xs="6" xl="3">
											<div className="tp-stat-card tp-stat-card--payable">
												<div className="tp-stat-label">{t("Invoice Total")}</div>
												<div className="tp-stat-value">
													{formatAmount(
														collectAmountBreakdown.invoiceTotal,
														currency,
														getCurrencySymbol
													)}
												</div>
											</div>
										</Col>
										<Col xs="6" xl="3">
											<div className="tp-stat-card tp-stat-card--collected">
												<div className="tp-stat-label">{t("Paid")}</div>
												<div className="tp-stat-value tp-stat-value--success">
													{formatAmount(
														collectAmountBreakdown.paid,
														currency,
														getCurrencySymbol
													)}
												</div>
											</div>
										</Col>
										<Col xs="6" xl="3">
											<div className="tp-stat-card tp-stat-card--balance">
												<div className="tp-stat-label">{t("Balance Due")}</div>
												<div className="tp-stat-value tp-stat-value--danger">
													{formatAmount(
														collectAmountBreakdown.due,
														currency,
														getCurrencySymbol
													)}
												</div>
											</div>
										</Col>
									</Row>

									{additionalServices.length > 0 && (
										<div className="tp-section-box mb-3">
											<div className="tp-section-title">
												<i className="fa fa-plus-square-o" />
												<span>{t("Consultation & Additional Services")}</span>
											</div>
											<div className="tp-section-body table-responsive">
												<Table className="tp-table mb-0">
													<thead>
														<tr>
															<th>#</th>
															<th>{t("Service")}</th>
															<th>{t("Type")}</th>
															<th className="text-center">{t("Qty")}</th>
															<th className="text-end">{t("Price")}</th>
															<th className="text-end">{t("Amount")}</th>
														</tr>
													</thead>
													<tbody>
														{additionalServicesWithTax.map((service, index) => (
															<tr key={service.id || index}>
																<td>{index + 1}</td>
																<td className="fw-semibold">{service.service_name}</td>
																<td style={{ textTransform: "capitalize" }}>
																	{formatInvoiceType(service.service_type)}
																</td>
																<td className="text-center">{service.quantity}</td>
																<td className="text-end">
																	{formatAmount(
																		service.unit_price ?? service.price,
																		currency,
																		getCurrencySymbol
																	)}
																</td>
																<td className="text-end fw-semibold">
																	{formatAmount(
																		service.line_base ??
																			service.display_amount ??
																			service.amount,
																		currency,
																		getCurrencySymbol
																	)}
																</td>
															</tr>
														))}
													</tbody>
													<ServiceSectionFooter
														t={t}
														subtotal={additionalServicesSection.subtotal}
														discount={additionalServicesSection.discount}
														tax={additionalServicesSection.tax}
														netTotal={additionalServicesSection.netTotal}
														currency={currency}
														getCurrencySymbol={getCurrencySymbol}
														taxLabel={
															String(pharmacyPOS?.taxType || "").toLowerCase() === "vat"
																? t("VAT")
																: t("Tax")
														}
													/>
												</Table>
											</div>
										</div>
									)}

									{labInvestigations.length > 0 && (
										<div className="tp-section-box mb-3">
											<div className="tp-section-title">
												<i className="fa fa-flask" />
												<span>{t("Laboratory")}</span>
											</div>
											<div className="tp-section-body table-responsive">
												<Table className="tp-table mb-0">
													<thead>
														<tr>
															<th>#</th>
															<th>{t("Service")}</th>
															<th>{t("Type")}</th>
															<th className="text-center">{t("Qty")}</th>
															<th className="text-end">{t("Price")}</th>
															<th className="text-end">{t("Amount")}</th>
														</tr>
													</thead>
													<tbody>
														{labInvestigations.map((test, index) => (
															<tr key={test.id || test.test_id || index}>
																<td>{index + 1}</td>
																<td className="fw-semibold">{test.test_name}</td>
																<td style={{ textTransform: "capitalize" }}>
																	{formatInvoiceType(test.type)}
																</td>
																<td className="text-center">{test.quantity}</td>
																<td className="text-end">
																	{test.priceResolved ? (
																		formatAmount(
																			test.unit_price,
																			currency,
																			getCurrencySymbol
																		)
																	) : (
																		<span
																			className="text-muted fst-italic"
																			title={t(
																				"Price not set in master. It can be entered while collecting payment."
																			)}
																		>
																			{t("N/A")}
																		</span>
																	)}
																</td>
																<td className="text-end fw-semibold">
																	{test.priceResolved ? (
																		formatAmount(
																			test.line_base ??
																				test.display_amount ??
																				test.amount,
																			currency,
																			getCurrencySymbol
																		)
																	) : (
																		<span
																			className="text-muted fst-italic"
																			title={t(
																				"Amount not set in master. It can be entered while collecting payment."
																			)}
																		>
																			{t("N/A")}
																		</span>
																	)}
																</td>
															</tr>
														))}
													</tbody>
													{labInvestigationsSection.showSubtotalRow && (
														<ServiceSectionFooter
															t={t}
															subtotal={labInvestigationsSection.subtotal}
															discount={labInvestigationsSection.discount}
															tax={labInvestigationsSection.tax}
															netTotal={labInvestigationsSection.netTotal}
															currency={currency}
															getCurrencySymbol={getCurrencySymbol}
															taxLabel={
																String(pharmacyPOS?.taxType || "").toLowerCase() === "vat"
																	? t("VAT")
																	: t("Tax")
															}
														/>
													)}
												</Table>
											</div>
										</div>
									)}

									{showPrescriptionSection && (
										<div className="tp-section-box mb-3">
											<div className="tp-section-title">
												<i className="fa fa-medkit" />
												<span>{t("Prescription Medicines")}</span>
											</div>
											<div className="tp-section-body table-responsive billing-payment-modal patient-account-prescription-table">
												<BillingPharmacyMedicinesTable
													t={t}
													cart={[]}
													priorPharmacyItems={prescriptionBillingItems}
													itemTaxes={{}}
													totals={{}}
													discountType={pharmacyDetailTableProps.discountType}
													discountValue={pharmacyDetailTableProps.discountValue}
													posTaxType={pharmacyDetailTableProps.posTaxType}
													pharmacyStateCode={pharmacyDetailTableProps.pharmacyStateCode}
													patientStateCode={pharmacyDetailTableProps.patientStateCode}
													displayCurrency={pharmacyDetailTableProps.displayCurrency}
													pharmacyCurrency={pharmacyDetailTableProps.pharmacyCurrency}
													convertCurrency={convertCurrency}
													getCurrencySymbol={getCurrencySymbol}
													showPosTax
												/>
											</div>
										</div>
									)}

									<Row className="g-3">
										<Col lg="8">
											<div className="tp-section-box h-100">
												<div className="tp-section-title tp-section-title--bar">
													<i className="fa fa-history" />
													<span>{t("Payment History")}</span>
												</div>
												<div className="tp-section-body table-responsive">
													<Table className="tp-table mb-0">
														<thead>
															<tr>
																<th>{t("Date")}</th>
																<th>{t("Type")}</th>
																<th>{t("Description")}</th>
																<th className="text-end">{t("Paid Amount")}</th>
																<th>{t("Payment Method")}</th>
															</tr>
														</thead>
														<tbody>
															{treatmentPaymentHistory.length > 0 ? (
																treatmentPaymentHistory.map((payment, index) => (
																	<tr key={index}>
																		<td>{formatDateList(payment.date)}</td>
																		<td>{payment.type}</td>
																		<td
																			className="tp-muted-cell"
																			style={{ whiteSpace: "pre-line" }}
																		>
																			{payment.description}
																		</td>
																		<td className="text-end fw-semibold">
																			{payment.is_payment
																				? formatPaymentHistoryPaidAmount(
																						payment,
																						getCurrencySymbol,
																						currency
																				  )
																				: ""}
																		</td>
																		<td style={{ textTransform: "capitalize" }}>
																			{payment.is_payment
																				? payment.payment_mode || "-"
																				: ""}
																		</td>
																	</tr>
																))
															) : (
																<tr>
																	<td colSpan="5" className="text-center text-muted">
																		{t("No billing history available.")}
																	</td>
																</tr>
															)}
														</tbody>
														{treatmentPaymentHistory.length > 0 && (
															<PaymentHistoryFooter
																t={t}
																variant="treatment"
																totalAmount={financialSummary.total_paid}
																currency={currency}
																foreignPayments={financialSummary.foreign_payments}
																getCurrencySymbol={getCurrencySymbol}
															/>
														)}
													</Table>
												</div>
											</div>
										</Col>
										<Col lg="4">
											<div className="summary-box h-100">
												<h6 className="summary-box-title">{t("Invoice Summary")}</h6>
												<div className="summary-box-body">
													<div className="summary-row">
														<span>{t("Package Price")}</span>
														<strong>
															{formatAmount(
																collectAmountBreakdown.packagePrice,
																currency,
																getCurrencySymbol
															)}
														</strong>
													</div>
													{collectAmountBreakdown.showAdditionalServices && (
														<div className="summary-row">
															<span>{t("Additional Services")}</span>
															<strong>
																{formatAmount(
																	collectAmountBreakdown.servicesTotal,
																	currency,
																	getCurrencySymbol
																)}
															</strong>
														</div>
													)}
													{collectAmountBreakdown.showLabSection && (
														<div className="summary-row">
															<span>{t("Lab & Investigations")}</span>
															<strong>
																{formatAmount(
																	labInvestigationsTotal,
																	currency,
																	getCurrencySymbol
																)}
															</strong>
														</div>
													)}
													{collectAmountBreakdown.showPharmacy && (
														<div className="summary-row">
															<span>{t("Pharmacy Purchases")}</span>
															<strong>
																{formatAmount(
																	collectAmountBreakdown.pharmacyTotal,
																	currency,
																	getCurrencySymbol
																)}
															</strong>
														</div>
													)}
													<div className="summary-row net-total-row">
														<span>{t("Invoice Total")}</span>
														<strong>
															{formatAmount(
																collectAmountBreakdown.invoiceTotal,
																currency,
																getCurrencySymbol
															)}
														</strong>
													</div>
													<div className="summary-row collected-row">
														<span>{t("Paid")}</span>
														<strong className="tp-summary-collected">
															{formatAmount(
																collectAmountBreakdown.paid,
																currency,
																getCurrencySymbol
															)}
														</strong>
													</div>
													<div className="summary-row due-row">
														<span>{t("Balance Due")}</span>
														<strong className="due-value">
															{formatAmount(
																collectAmountBreakdown.due,
																currency,
																getCurrencySymbol
															)}
														</strong>
													</div>
												</div>
											</div>
										</Col>
									</Row>
								</div>
							) : (
								<div className="tp-view">
									<div className="tp-meta-strip mb-3">
										<div className="tp-meta-item">
											<span className="tp-meta-label">{t("Invoice Number")}</span>
											<span className="tp-meta-value">
												{skeletonLoading ? (
													<Skeleton width={130} />
												) : (
													normalized?.invoice_no || "-"
												)}
											</span>
										</div>
										<div className="tp-meta-item">
											<span className="tp-meta-label">{t("Invoice Date")}</span>
											<span className="tp-meta-value">
												{skeletonLoading ? (
													<Skeleton width={150} />
												) : (
													formatDateTime(normalized?.invoice_date)
												)}
											</span>
										</div>
										<div className="tp-meta-item">
											<span className="tp-meta-label">{t("Payment Status")}</span>
											<span className="tp-meta-value">
												{skeletonLoading ? (
													<Skeleton width={90} height={22} />
												) : (
													<InvoiceStatusBadge
														status={paymentStatus}
														isSettled={record?.appointment_invoice?.is_settled === true}
													/>
												)}
											</span>
										</div>
									</div>

									<Row className="g-3 mb-3">
										<Col md="6" xl="4">
											<div className="tp-details-card">
												<div className="tp-details-card-title">
													<i className="fa fa-user-o" />
													<span>{t("Patient Details")}</span>
												</div>
												<div className="tp-details-card-body">
													<div className="tp-detail-row">
														<span className="tp-detail-label">{t("Name")}</span>
														<span className="tp-detail-value">
															{skeletonLoading ? <Skeleton /> : patientName}
														</span>
													</div>
													<div className="tp-detail-row">
														<span className="tp-detail-label">{t("File No.")}</span>
														<span className="tp-detail-value">
															{skeletonLoading ? <Skeleton /> : fileNumber}
														</span>
													</div>
													<div className="tp-detail-row">
														<span className="tp-detail-label">{t("Address")}</span>
														<span className="tp-detail-value">
															{skeletonLoading ? <Skeleton /> : patientAddress}
														</span>
													</div>
													<div className="tp-detail-row">
														<span className="tp-detail-label">{t("Contact")}</span>
														<span className="tp-detail-value">
															{skeletonLoading ? <Skeleton /> : contactNumber}
														</span>
													</div>
													<div className="tp-detail-row">
														<span className="tp-detail-label">{t("Gender")}</span>
														<span className="tp-detail-value">
															{skeletonLoading ? <Skeleton /> : patientGender}
														</span>
													</div>
												</div>
											</div>
										</Col>
										<Col md="6" xl="4">
											<div className="tp-details-card">
												<div className="tp-details-card-title">
													<i className="fa fa-stethoscope" />
													<span>{t("Doctor Details")}</span>
												</div>
												<div className="tp-details-card-body">
													<div className="tp-detail-row">
														<span className="tp-detail-label">{t("Name")}</span>
														<span className="tp-detail-value">
															{skeletonLoading ? <Skeleton /> : doctorName}
														</span>
													</div>
													<div className="tp-detail-row">
														<span className="tp-detail-label">{t("Contact")}</span>
														<span className="tp-detail-value">
															{skeletonLoading ? <Skeleton /> : doctorContact}
														</span>
													</div>
													<div className="tp-detail-row">
														<span className="tp-detail-label">{t("Email")}</span>
														<span className="tp-detail-value">
															{skeletonLoading ? <Skeleton /> : doctorEmail}
														</span>
													</div>
													<div className="tp-detail-row">
														<span className="tp-detail-label">{t("Gender")}</span>
														<span className="tp-detail-value">
															{skeletonLoading ? <Skeleton /> : doctorGender}
														</span>
													</div>
												</div>
											</div>
										</Col>
										<Col md="12" xl="4">
											<div className="tp-details-card">
												<div className="tp-details-card-title">
													<i className="fa fa-heartbeat" />
													<span>{t("Consultation Details")}</span>
												</div>
												<div className="tp-details-card-body">
													<div className="tp-detail-row">
														<span className="tp-detail-label">{t("Consultation Reason")}</span>
														<span className="tp-detail-value">
															{skeletonLoading ? (
																<Skeleton />
															) : (
																record?.consultation_reason ||
																record?.consultation?.consultation_reason ||
																"-"
															)}
														</span>
													</div>
													<div className="tp-detail-row">
														<span className="tp-detail-label">{t("Type")}</span>
														<span className="tp-detail-value">
															{skeletonLoading ? (
																<Skeleton />
															) : (
																formatInvoiceType(
																	record?.appointment_invoice?.invoice_type ||
																		"consultation"
																)
															)}
														</span>
													</div>
													<div className="tp-detail-row">
														<span className="tp-detail-label">{t("Appointment Date")}</span>
														<span className="tp-detail-value">
															{skeletonLoading ? (
																<Skeleton />
															) : (
																formatDateList(
																	record?.appointment?.appointment_date ||
																		record?.appointment_date ||
																		normalized?.invoice_date
																)
															)}
														</span>
													</div>
													<div className="tp-detail-row">
														<span className="tp-detail-label">{t("Email")}</span>
														<span className="tp-detail-value">
															{skeletonLoading ? <Skeleton /> : email}
														</span>
													</div>
													<div className="tp-detail-row">
														<span className="tp-detail-label">{t("Clinic")}</span>
														<span className="tp-detail-value">
															{skeletonLoading ? (
																<Skeleton />
															) : (
																pharmacyPOS?.hospital?.name || "-"
															)}
														</span>
													</div>
												</div>
											</div>
										</Col>
									</Row>

									<Row className="g-3 mb-3">
										<Col xs="6" xl="3">
											<div className="tp-stat-card tp-stat-card--price">
												<div className="tp-stat-label">
													{t("Clinic Consultation & Services")}
												</div>
												<div className="tp-stat-value">
													{formatAmount(
														collectAmountBreakdown.servicesTotal,
														currency,
														getCurrencySymbol
													)}
												</div>
											</div>
										</Col>
										<Col xs="6" xl="3">
											<div className="tp-stat-card tp-stat-card--payable">
												<div className="tp-stat-label">{t("Invoice Total")}</div>
												<div className="tp-stat-value">
													{formatAmount(
														collectAmountBreakdown.invoiceTotal,
														currency,
														getCurrencySymbol
													)}
												</div>
											</div>
										</Col>
										<Col xs="6" xl="3">
											<div className="tp-stat-card tp-stat-card--collected">
												<div className="tp-stat-label">{t("Paid")}</div>
												<div className="tp-stat-value tp-stat-value--success">
													{formatAmount(
														collectAmountBreakdown.paid,
														currency,
														getCurrencySymbol
													)}
												</div>
											</div>
										</Col>
										<Col xs="6" xl="3">
											<div className="tp-stat-card tp-stat-card--balance">
												<div className="tp-stat-label">{t("Balance Due")}</div>
												<div className="tp-stat-value tp-stat-value--danger">
													{formatAmount(
														collectAmountBreakdown.due,
														currency,
														getCurrencySymbol
													)}
												</div>
											</div>
										</Col>
									</Row>

									{additionalServices.length > 0 && (
										<div className="tp-section-box mb-3">
											<div className="tp-section-title">
												<i className="fa fa-plus-square-o" />
												<span>{t("Consultation & Additional Services")}</span>
											</div>
											<div className="tp-section-body table-responsive">
												<Table className="tp-table mb-0">
													<thead>
														<tr>
															<th>#</th>
															<th>{t("Service")}</th>
															<th>{t("Type")}</th>
															<th className="text-center">{t("Qty")}</th>
															<th className="text-end">{t("Price")}</th>
															<th className="text-end">{t("Amount")}</th>
														</tr>
													</thead>
													<tbody>
														{additionalServicesWithTax.map((service, index) => (
															<tr key={service.id || index}>
																<td>{index + 1}</td>
																<td className="fw-semibold">{service.service_name}</td>
																<td style={{ textTransform: "capitalize" }}>
																	{formatInvoiceType(service.service_type)}
																</td>
																<td className="text-center">{service.quantity}</td>
																<td className="text-end">
																	{formatAmount(
																		service.unit_price ?? service.price ?? service.charges,
																		currency,
																		getCurrencySymbol
																	)}
																</td>
																<td className="text-end fw-semibold">
																	{formatAmount(
																		service.line_base ??
																			service.display_amount ??
																			service.amount,
																		currency,
																		getCurrencySymbol
																	)}
																</td>
															</tr>
														))}
													</tbody>
													<ServiceSectionFooter
														t={t}
														subtotal={additionalServicesSection.subtotal}
														discount={additionalServicesSection.discount}
														tax={additionalServicesSection.tax}
														netTotal={additionalServicesSection.netTotal}
														currency={currency}
														getCurrencySymbol={getCurrencySymbol}
														taxLabel={
															String(pharmacyPOS?.taxType || "").toLowerCase() === "vat"
																? t("VAT")
																: t("Tax")
														}
													/>
												</Table>
											</div>
										</div>
									)}

									{labInvestigations.length > 0 && (
										<div className="tp-section-box mb-3">
											<div className="tp-section-title">
												<i className="fa fa-flask" />
												<span>{t("Laboratory")}</span>
											</div>
											<div className="tp-section-body table-responsive">
												<Table className="tp-table mb-0">
													<thead>
														<tr>
															<th>#</th>
															<th>{t("Service")}</th>
															<th>{t("Type")}</th>
															<th className="text-center">{t("Qty")}</th>
															<th className="text-end">{t("Price")}</th>
															<th className="text-end">{t("Amount")}</th>
														</tr>
													</thead>
													<tbody>
														{labInvestigations.map((test, index) => (
															<tr key={test.id || test.test_id || index}>
																<td>{index + 1}</td>
																<td className="fw-semibold">{test.test_name}</td>
																<td style={{ textTransform: "capitalize" }}>
																	{formatInvoiceType(test.type)}
																</td>
																<td className="text-center">{test.quantity}</td>
																<td className="text-end">
																	{test.priceResolved ? (
																		formatAmount(
																			test.unit_price,
																			currency,
																			getCurrencySymbol
																		)
																	) : (
																		<span
																			className="text-muted fst-italic"
																			title={t(
																				"Price not set in master. It can be entered while collecting payment."
																			)}
																		>
																			{t("N/A")}
																		</span>
																	)}
																</td>
																<td className="text-end fw-semibold">
																	{test.priceResolved ? (
																		formatAmount(
																			test.line_base ??
																				test.display_amount ??
																				test.amount,
																			currency,
																			getCurrencySymbol
																		)
																	) : (
																		<span
																			className="text-muted fst-italic"
																			title={t(
																				"Amount not set in master. It can be entered while collecting payment."
																			)}
																		>
																			{t("N/A")}
																		</span>
																	)}
																</td>
															</tr>
														))}
													</tbody>
													{labInvestigationsSection.showSubtotalRow && (
														<ServiceSectionFooter
															t={t}
															subtotal={labInvestigationsSection.subtotal}
															discount={labInvestigationsSection.discount}
															tax={labInvestigationsSection.tax}
															netTotal={labInvestigationsSection.netTotal}
															currency={currency}
															getCurrencySymbol={getCurrencySymbol}
															taxLabel={
																String(pharmacyPOS?.taxType || "").toLowerCase() === "vat"
																	? t("VAT")
																	: t("Tax")
															}
														/>
													)}
												</Table>
											</div>
										</div>
									)}

									{showPrescriptionSection && (
										<div className="tp-section-box mb-3">
											<div className="tp-section-title">
												<i className="fa fa-medkit" />
												<span>{t("Prescription Medicines")}</span>
											</div>
											<div className="tp-section-body table-responsive billing-payment-modal patient-account-prescription-table">
												<BillingPharmacyMedicinesTable
													t={t}
													cart={[]}
													priorPharmacyItems={prescriptionBillingItems}
													itemTaxes={{}}
													totals={{}}
													discountType={pharmacyDetailTableProps.discountType}
													discountValue={pharmacyDetailTableProps.discountValue}
													posTaxType={pharmacyDetailTableProps.posTaxType}
													pharmacyStateCode={pharmacyDetailTableProps.pharmacyStateCode}
													patientStateCode={pharmacyDetailTableProps.patientStateCode}
													displayCurrency={pharmacyDetailTableProps.displayCurrency}
													pharmacyCurrency={pharmacyDetailTableProps.pharmacyCurrency}
													convertCurrency={convertCurrency}
													getCurrencySymbol={getCurrencySymbol}
													showPosTax
												/>
											</div>
										</div>
									)}

									<Row className="g-3">
										<Col lg="8">
											<div className="tp-section-box h-100">
												<div className="tp-section-title tp-section-title--bar">
													<i className="fa fa-history" />
													<span>{t("Payment History")}</span>
												</div>
												<div className="tp-section-body table-responsive">
													<Table className="tp-table mb-0">
														<thead>
															<tr>
																<th>#</th>
																<th>{t("Payment Date")}</th>
																<th className="text-end">{t("Paid Amount")}</th>
																<th>{t("Payment Method")}</th>
																<th>{t("Receipt No.")}</th>
															</tr>
														</thead>
														<tbody>
															{paymentHistory.length > 0 ? (
																paymentHistory.map((payment, index) => (
																	<tr key={index}>
																		<td>{index + 1}</td>
																		<td>{formatDateTime(payment.payment_date)}</td>
																		<td className="text-end fw-semibold">
																			{formatPaymentHistoryPaidAmount(
																				payment,
																				getCurrencySymbol,
																				currency
																			)}
																		</td>
																		<td style={{ textTransform: "capitalize" }}>
																			{payment.payment_mode || "-"}
																		</td>
																		<td>{payment.receipt_number || "-"}</td>
																	</tr>
																))
															) : (
																<tr>
																	<td colSpan="5" className="text-center text-muted">
																		{t("No billing history available.")}
																	</td>
																</tr>
															)}
														</tbody>
														{paymentHistory.length > 0 && (
															<PaymentHistoryFooter
																t={t}
																totalAmount={financialSummary.total_paid}
																currency={currency}
																foreignPayments={financialSummary.foreign_payments}
																getCurrencySymbol={getCurrencySymbol}
															/>
														)}
													</Table>
												</div>
											</div>
										</Col>
										<Col lg="4">
											<div className="summary-box h-100">
												<h6 className="summary-box-title">{t("Invoice Summary")}</h6>
												<div className="summary-box-body">
													<div className="summary-row">
														<span>{t("Clinic Consultation & Services")}</span>
														<strong>
															{formatAmount(
																collectAmountBreakdown.servicesTotal,
																currency,
																getCurrencySymbol
															)}
														</strong>
													</div>
													{collectAmountBreakdown.showLabSection && (
														<div className="summary-row">
															<span>{t("Lab & Investigations")}</span>
															<strong>
																{formatAmount(
																	labInvestigationsTotal,
																	currency,
																	getCurrencySymbol
																)}
															</strong>
														</div>
													)}
													{collectAmountBreakdown.showPharmacy && (
														<div className="summary-row">
															<span>{t("Pharmacy Purchases")}</span>
															<strong>
																{formatAmount(
																	collectAmountBreakdown.pharmacyTotal,
																	currency,
																	getCurrencySymbol
																)}
															</strong>
														</div>
													)}
													<div className="summary-row net-total-row">
														<span>{t("Invoice Total")}</span>
														<strong>
															{formatAmount(
																collectAmountBreakdown.invoiceTotal,
																currency,
																getCurrencySymbol
															)}
														</strong>
													</div>
													<div className="summary-row collected-row">
														<span>{t("Paid")}</span>
														<strong className="tp-summary-collected">
															{formatAmount(
																collectAmountBreakdown.paid,
																currency,
																getCurrencySymbol
															)}
														</strong>
													</div>
													<div className="summary-row due-row">
														<span>{t("Balance Due")}</span>
														<strong className="due-value">
															{formatAmount(
																collectAmountBreakdown.due,
																currency,
																getCurrencySymbol
															)}
														</strong>
													</div>
												</div>
											</div>
										</Col>
									</Row>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>

			<InvoicePreviewModal
				isOpen={previewModal}
				toggle={closeDocumentPreview}
				invoiceUrl={previewUrl}
				invoiceNo={normalized?.invoice_no}
				title={previewTitle}
			/>

			<CollectPaymentModal
				isOpen={collectModal}
				toggle={() => setCollectModal(false)}
				invoice={record}
				onSubmit={handleCollectSubmit}
				isSubmitting={isSubmittingPayment}
			/>
		</>
	);
};

export default PatientAccountDetails;
